/**
 * The composition root: where input sources are owned, and the only place the running
 * app assembles a live session.
 *
 * Everything below this file is a pure function of `SessionView` — that is what makes
 * `/dev/states` an honest preview and the fifteen UX states renderable from fixtures.
 * Everything *around* it is impure: a microphone, an `AudioContext`, IndexedDB, the
 * network. This is the seam, and it is deliberately one file rather than a scatter of
 * hooks, because the questions it answers are all the same question — *who owns the
 * input, and how does a chord get into the reducer?*
 *
 * ## One funnel, two kinds of input
 *
 * A microphone is a **continuous stream**: it produces detections on its own schedule and
 * speaks through `ChordRecognizer`. Future MIDI plugs in at exactly the same point. A
 * branch card tapped on a train is a **discrete selection**: no signal, no stability
 * machine, no uncertainty. Both go through `state/chordInput.ts` into the same
 * `CHORD_DETECTED`. There is no second harmony engine, no parallel reducer, and no "on
 * the go" branch anywhere in the session logic — the mode decides how a chord is chosen
 * and nothing else.
 *
 * ## What On the Go must never do
 *
 * `startOnTheGo` must not reach `getUserMedia`, and must construct no `AudioContext` of
 * its own. Not as an optimisation — on iOS, touching either outside a user gesture
 * produces a permission prompt or a permanently suspended context, and a person on a train
 * who wanted to think about harmony has instead been asked for their microphone. The
 * recogniser is therefore built lazily, on the first `startListening`, and the two entry
 * points share no code path that could grow one by accident. A test asserts it.
 *
 * The one context On the Go does now create is a **playback** one, inside the tap that
 * plays a chord — see `audio/chordPlayback.ts`. It is the opposite case: it exists only
 * because somebody tapped, so it is created on a gesture's own stack and never before, and
 * it can never lead to a permission prompt.
 *
 * ## Why the sound is hung off the store rather than off `selectChord`
 *
 * The picker, a tapped branch card, a typed symbol and an arpeggio completing under a
 * chord are four ways to reach one `CHORD_DETECTED`. Playing from the buttons would mean
 * four places to remember; playing from the *landing* means one. And it must never happen
 * at the piano, where the microphone is open and would hear the speaker.
 *
 * ## Why the effects are rebuilt rather than reused
 *
 * `SessionEffects.detach()` disposes its persistence, so an attach/detach/attach cycle —
 * which is exactly what `StrictMode` does in development — would leave the second
 * attachment writing into a disposed autosave. The runtime is therefore created inside
 * the effect and thrown away with it. The *store* is not: it is the session, and it
 * outlives any number of remounts.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DEFAULT_RECOGNIZER_PARAMS,
  createInitialSessionState,
  currentNode,
  type CanonicalChord,
  type ChordRecognizer,
  type PreferenceHints,
  type SessionState,
} from '@hc/shared';
import { playChord, warmUpChordPlayback } from '../audio/chordPlayback.js';
import { createMicManager, type MicManager } from '../audio/micManager.js';
import { createLiveRecognizer } from '../audio/recognizerLive.js';
import { explain } from '../api/index.js';
import type { ExplainRequest } from '../api/index.js';
import {
  createSketchFromSession,
  loadCollectRecognitions,
  loadHostSettings,
  resetPreferences as resetStoredPreferences,
  saveCollectRecognitions,
  saveHostSettings,
  updateSketch,
  type HostSettingsRecord,
  type NewSketch,
} from '../sketchbook/queries.js';
import {
  SessionProvider,
  type ExplanationFetcher,
  type SaveReceipt,
  type SessionMode,
  type SessionSettings,
  type SessionView,
} from '../components/system/sessionView.js';
import { commitChordSelection, commitDetection, performanceNow } from './chordInput.js';
import { recordDebugEntry } from './debugLog.js';
import { attachSessionEffects, type BranchesApi, type SessionEffects } from './effects.js';
import { heuristicExplanation } from './heuristicBranches.js';
import { createSessionPersistence, type SessionPersistence } from './persistence.js';
import { createPreferencesController, type PreferencesController } from './preferences.js';
import { DEFAULT_SESSION_MODE, loadSessionMode, saveSessionMode } from './modePreference.js';
import { getSessionStore, newSessionId, useSessionState, type SessionStore } from './store.js';

/** Owned by `components/system/sessionView.tsx`; re-exported so callers need one import. */
export type { SessionSettings };

export const DEFAULT_SESSION_SETTINGS: SessionSettings = {
  speakReplies: false,
  // Off, and off is the product. Branches, descriptors and explanations all come from
  // `@hc/shared`'s engine unless somebody asks for a model — instant, offline, and the
  // same wire shape, so nothing below this file can tell the difference. The LLM path is
  // whole and one switch away; see `heuristicBranches.ts` and the note in CLAUDE.md.
  aiCompanion: false,
  prefetch: true,
  collectRecognitions: false,
  // On, alone among these. On the Go without sound is a picture of music: the owner's
  // words were "otherwise this isn't particularly helpful".
  soundOnTheGo: true,
  // Luna, and it must stay the same tier `DEFAULT_MAIN_CHOICE` names on the server: this is
  // what an untouched settings sheet sends, that is what a request naming nothing gets, and
  // a disagreement between them would be a default that depends on which of the two spoke.
  modelChoice: 'luna',
  showPrompts: false,
};

export interface SessionHostOptions {
  /** Defaults to the application store. Tests pass their own so nothing leaks between them. */
  store?: SessionStore;
  /**
   * Input-source factories. Both are called lazily — never at mount — so a host that is
   * only ever used On the Go constructs neither.
   */
  createMic?: () => MicManager;
  createRecognizer?: () => ChordRecognizer;
  createPersistence?: () => SessionPersistence;
  createPreferences?: () => PreferencesController;
  /** The branch API the effects call. Injected in tests so nothing reaches the network. */
  api?: BranchesApi;
  /** The device's stored mode. Injected in tests; defaults to the `preferences` store. */
  loadMode?: () => Promise<SessionMode | null>;
  saveMode?: (mode: SessionMode) => Promise<void>;
  /** The device's stored collection choice. Injected in tests, same as the mode. */
  loadCollect?: () => Promise<boolean | null>;
  saveCollect?: (enabled: boolean) => Promise<void>;
  /** The rest of the device's stored settings: sound, model tier, prompts. */
  loadSettings?: () => Promise<HostSettingsRecord>;
  saveSettings?: (patch: HostSettingsRecord) => Promise<void>;
  /**
   * Play a chord that has just landed. Injected in tests, which have no Web Audio and no
   * wish to download a piano.
   */
  playChord?: (chord: CanonicalChord) => void;
  /** The clock discrete selections are stamped with. Must match the recogniser's. */
  now?: () => number;
  /** Set false to skip the cold-start snapshot lookup, and with it the resume card. */
  offerResume?: boolean;
  /** Route to a sketch. Supplied by `App`, which is inside the router; absent elsewhere. */
  navigate?: (to: string) => void;
  onError?: (error: unknown, context: string) => void;
}

interface HostRuntime {
  persistence: SessionPersistence;
  preferences: PreferencesController;
  effects: SessionEffects;
}

/**
 * The live `SessionView`: real store, real microphone, real storage.
 *
 * Returns the same shape `createIdleSessionView` does, with every optional capability
 * filled in. Nothing that renders needs to know which one it has.
 */
export function useLiveSessionView(options: SessionHostOptions = {}): SessionView {
  // Read through a ref so a caller passing inline factories does not re-attach the whole
  // effects layer on every render. Mutating during render is the same idiom `store.ts`
  // uses for its selector.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const { navigate } = options;
  const store = useMemo(() => options.store ?? getSessionStore(), [options.store]);
  const state = useSessionState(store);

  const [mode, setModeState] = useState<SessionMode>(DEFAULT_SESSION_MODE);
  const [resumeSnapshot, setResumeSnapshot] = useState<SessionState | null>(null);
  const [lastSave, setLastSave] = useState<SaveReceipt | null>(null);
  const [settings, setSettings] = useState<SessionSettings>(DEFAULT_SESSION_SETTINGS);
  /**
   * The same settings, readable without going through a render.
   *
   * The effects layer attaches once at mount and asks for the model tier and the debug
   * flag on every request; reading them from the captured `settings` would pin them to
   * whatever they were at mount. Mutated during render, the same idiom `optionsRef` uses
   * above.
   */
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  /** Flips once a recogniser exists, so `recognizerControls` can be honestly null before. */
  const [recognizerLive, setRecognizerLive] = useState(false);

  const runtimeRef = useRef<HostRuntime | null>(null);
  const micRef = useRef<MicManager | null>(null);
  const recognizerRef = useRef<ChordRecognizer | null>(null);
  const offChordRef = useRef<(() => void) | null>(null);
  // The snapshot is mirrored in a ref because the callbacks that consume it must read it
  // without going through a state updater, which React is free to run twice.
  const resumeRef = useRef<SessionState | null>(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  /**
   * Set once the player has chosen a mode with a gesture.
   *
   * The stored preference is an opening default and nothing more. A slow `preferences`
   * read must not overrule a tap that has already happened — and it would do more than
   * flip a label: applying it goes through `setModeState`, which skips `setMode`'s
   * pause/resume, so a device that last ran On the Go could land on the On the Go screen
   * with the microphone open and the recogniser still appending played chords.
   */
  const modeChosenRef = useRef(false);
  /** The same guard for the collection toggle: a slow read must not undo a tap. */
  const collectChosenRef = useRef(false);
  /** And for the three settings stored together — see `loadSettings` below. */
  const settingsChosenRef = useRef(false);
  /**
   * True while the microphone is handed back because the player went On the Go.
   *
   * The switch back to the piano is a user gesture and therefore the one moment a stream
   * can be reopened, but only this flag distinguishes "we released it and owe you a
   * re-acquisition" from "there was never one open" — and from `startListening`, which
   * routes through `setMode('piano')` on its way to acquiring for itself.
   */
  const micReleasedRef = useRef(false);

  const report = useCallback((error: unknown, context: string) => {
    optionsRef.current.onError?.(error, context);
  }, []);

  // -- the effects layer ----------------------------------------------------

  const prefetch = settings.prefetch;

  useEffect(() => {
    const { api, createPersistence, createPreferences, offerResume = true } = optionsRef.current;
    const persistence = createPersistence ? createPersistence() : createSessionPersistence();
    const preferences = createPreferences ? createPreferences() : createPreferencesController();
    const effects = attachSessionEffects({
      store,
      persistence,
      preferences,
      prefetch,
      onError: report,
      /*
       * Read through the ref at call time, never captured.
       *
       * This effect runs at mount; the recogniser is built lazily on the first
       * `startListening`, which is always later. Passing `recognizerRef.current` here —
       * or omitting the option, which is what this file used to do — stamps every
       * correction with `DEFAULT_RECOGNIZER_PARAMS` instead of the parameters actually in
       * force. That field is the whole point of the record: a correction logged against
       * the wrong parameters is worse than one not logged at all, because it looks true.
       */
      getRecognizerParams: () => recognizerRef.current?.getParams() ?? DEFAULT_RECOGNIZER_PARAMS,
      // Read at call time for the same reason, and through the same kind of ref: the tier
      // is a setting somebody can change between two chords.
      getModelChoice: () => settingsRef.current.modelChoice,
      // And so is which engine answers at all. Off, `ensureBranches` never reaches the
      // network and the look-ahead never runs.
      getAiCompanion: () => settingsRef.current.aiCompanion,
      getDebug: () => settingsRef.current.showPrompts,
      onDebug: (route, debug) => recordDebugEntry(route, debug),
      ...(api ? { api } : {}),
    });
    runtimeRef.current = { persistence, preferences, effects };

    let cancelled = false;
    if (offerResume) {
      void persistence
        .loadLatest()
        .then((snapshot) => {
          // An empty session is not worth offering back, and neither is the one already
          // running — the resume card exists for work that would otherwise be lost.
          if (cancelled || !snapshot || snapshot.path.length === 0) return;
          if (snapshot.sessionId === store.getState().sessionId) return;
          resumeRef.current = snapshot;
          setResumeSnapshot(snapshot);
        })
        .catch((error: unknown) => report(error, 'resume'));
    }

    return () => {
      cancelled = true;
      effects.detach();
      preferences.dispose();
      runtimeRef.current = null;
    };
  }, [store, prefetch, report]);

  /** The device's remembered mode. Separate from the runtime: it outlives any session. */
  useEffect(() => {
    let cancelled = false;
    const load = optionsRef.current.loadMode ?? loadSessionMode;
    void load()
      .then((stored) => {
        if (!cancelled && stored && !modeChosenRef.current) setModeState(stored);
      })
      .catch((error: unknown) => report(error, 'mode-preference'));
    return () => {
      cancelled = true;
    };
  }, [report]);

  /**
   * The device's remembered collection choice. A device preference like the mode: whether
   * this iPad is being used to harvest a dataset is a fact about the sitting, not the music.
   */
  useEffect(() => {
    let cancelled = false;
    const load = optionsRef.current.loadCollect ?? loadCollectRecognitions;
    void load()
      .then((stored) => {
        if (cancelled || stored === null || collectChosenRef.current) return;
        setSettings((current) => ({ ...current, collectRecognitions: stored }));
      })
      .catch((error: unknown) => report(error, 'collect-preference'));
    return () => {
      cancelled = true;
    };
  }, [report]);

  /**
   * The remembered adventurousness, written whenever the player sets it — from the home
   * screen's slider or the drawer's, which are one action — and put back on a cold start.
   *
   * Put back through `RESTORED` of the fresh state rather than through
   * `SET_ADVENTUROUSNESS`, because that action is also what feeds
   * `preferences.recordAdventurousness`, and a page load is not a choice. And only while
   * nothing has been played and nothing set since the page opened: a slow read must never
   * overrule a slider somebody has already moved, or a session already under way.
   */
  const adventurousnessChosenRef = useRef(false);
  useEffect(
    () =>
      store.subscribe((state, action) => {
        if (action.type !== 'SET_ADVENTUROUSNESS') return;
        adventurousnessChosenRef.current = true;
        const save = optionsRef.current.saveSettings ?? saveHostSettings;
        void save({ adventurousness: state.adventurousness }).catch((error: unknown) =>
          report(error, 'host-settings'),
        );
      }),
    [report, store],
  );
  const seedAdventurousness = useCallback(
    (stored: number | undefined) => {
      if (stored === undefined || adventurousnessChosenRef.current) return;
      const state = store.getState();
      if (state.path.length > 0 || state.adventurousness === stored) return;
      store.dispatch({ type: 'RESTORED', state: { ...state, adventurousness: stored } });
    },
    [store],
  );

  /**
   * The rest of the device's remembered settings: sound On the Go, the model tier, whether
   * the prompts are on show. Same race as the mode and the collection toggle — a slow read
   * must never overrule a choice already made — and the same guard.
   */
  useEffect(() => {
    let cancelled = false;
    const load = optionsRef.current.loadSettings ?? loadHostSettings;
    void load()
      .then((stored) => {
        if (cancelled) return;
        seedAdventurousness(stored.adventurousness);
        if (settingsChosenRef.current) return;
        // Only fields that are actually present: a record written before a setting existed
        // must leave that setting at its default rather than at `undefined`.
        setSettings((current) => ({
          ...current,
          ...(typeof stored.soundOnTheGo === 'boolean'
            ? { soundOnTheGo: stored.soundOnTheGo }
            : {}),
          ...(typeof stored.aiCompanion === 'boolean' ? { aiCompanion: stored.aiCompanion } : {}),
          ...(typeof stored.showPrompts === 'boolean' ? { showPrompts: stored.showPrompts } : {}),
          ...(stored.modelChoice ? { modelChoice: stored.modelChoice } : {}),
        }));
      })
      .catch((error: unknown) => report(error, 'host-settings'));
    return () => {
      cancelled = true;
    };
  }, [report, seedAdventurousness]);

  /**
   * Hearing the chord you just chose.
   *
   * Subscribed to the store rather than hung off `selectChord`, because the seam that
   * matters is the chord *landing*, not the button that caused it: the picker, a tapped
   * branch card, a typed symbol and a chord that grew under an arpeggio are four ways to
   * reach one `CHORD_DETECTED`, and hooking the buttons would mean four places to forget.
   *
   * **Piano mode never plays.** The microphone is open and would hear the speaker, feeding
   * the recogniser its own output — a feedback loop that would appear as the app playing
   * chords at itself. Mode is checked through a ref rather than captured, since this
   * subscription outlives any particular mode.
   *
   * The store notifies synchronously inside `dispatch`, which is itself inside the tap
   * handler, so the first play is on the gesture's own stack — which is the only moment
   * iOS will let an `AudioContext` start.
   */
  useEffect(() => {
    return store.subscribe((state, action, previous) => {
      if (action.type !== 'CHORD_DETECTED') return;
      if (modeRef.current !== 'on-the-go' || !settingsRef.current.soundOnTheGo) return;
      const node = currentNode(state);
      if (!node) return;
      // Appended, or replaced in place by an arpeggio completing. A chord merely being
      // sustained changes neither and must not re-strike.
      const before = currentNode(previous);
      const landed = before?.id !== node.id || before.chord.symbol !== node.chord.symbol;
      if (!landed) return;
      const play = optionsRef.current.playChord ?? playChord;
      play(node.chord);
    });
  }, [store]);

  /**
   * Fetch the piano the moment the player goes On the Go with sound on.
   *
   * Downloading Tone and 412 KB of samples takes longer than the gap between choosing the
   * mode and choosing a first chord, and a first chord that lands in silence and plays a
   * second later reads as broken. This makes no sound and creates no `AudioContext` — see
   * the note in `audio/chordPlayback.ts` — so it is safe outside a gesture, though in
   * practice it always follows one.
   */
  useEffect(() => {
    if (mode === 'on-the-go' && settings.soundOnTheGo && !optionsRef.current.playChord) {
      warmUpChordPlayback();
    }
  }, [mode, settings.soundOnTheGo]);

  /** Nothing here is owned by React, so it all has to be handed back explicitly. */
  useEffect(
    () => () => {
      offChordRef.current?.();
      offChordRef.current = null;
      void recognizerRef.current?.stop();
      recognizerRef.current = null;
      micRef.current?.release();
      micRef.current = null;
    },
    [],
  );

  // -- input sources --------------------------------------------------------

  const ensureRecognizer = useCallback((): ChordRecognizer => {
    const existing = recognizerRef.current;
    if (existing) return existing;
    const factory = optionsRef.current.createRecognizer;
    const recognizer = factory ? factory() : createLiveRecognizer();
    // The only recogniser event session logic is allowed to consume. `state` and
    // `diagnostics` belong to the tuning screen; reacting to them here would re-render
    // the graph at 10 Hz for nothing.
    offChordRef.current = recognizer.on((event) => {
      if (event.type !== 'chord') return;
      commitDetection(store, event.detected);
    });
    recognizerRef.current = recognizer;
    setRecognizerLive(true);
    return recognizer;
  }, [store]);

  const ensureMic = useCallback((): MicManager => {
    const existing = micRef.current;
    if (existing) return existing;
    const factory = optionsRef.current.createMic;
    const mic = factory ? factory() : createMicManager();
    micRef.current = mic;
    return mic;
  }, []);

  /**
   * Open the microphone and hand the stream to the recogniser.
   *
   * **Must be called on a user gesture's own stack.** `recognizer.start(null)` constructs
   * and resumes the `AudioContext`, and iOS will not resume one later; the permission
   * prompt that follows ends the gesture, so the context has to exist before it.
   *
   * Both entry points to listening use this: the first `startListening`, and the toggle
   * back from On the Go — which is a full re-acquisition rather than a resume, because
   * leaving for On the Go stops the recogniser, and `stop()` closes the context.
   *
   * It never dispatches `SESSION_STARTED`. Deciding whether there is a session to start
   * belongs to the caller, and getting that wrong resets the path.
   */
  const openMicrophone = useCallback(
    async (recognizer: ChordRecognizer) => {
      try {
        await recognizer.start(null);
      } catch (error) {
        report(error, 'audio');
      }

      const acquisition = await ensureMic().request();
      store.dispatch({ type: 'MIC_STATUS', status: acquisition.status });
      if (!acquisition.stream) {
        if (acquisition.error) report(acquisition.error, 'microphone');
        return;
      }
      try {
        await recognizer.start(acquisition.stream);
      } catch (error) {
        report(error, 'recognizer');
      }
    },
    [ensureMic, report, store],
  );

  // -- starting ------------------------------------------------------------

  const startSession = useCallback(
    (intention?: string) => {
      const trimmed = intention?.trim();
      store.dispatch({
        type: 'SESSION_STARTED',
        sessionId: newSessionId(),
        perspectives: store.getState().perspectives,
        ...(trimmed ? { intention: trimmed } : {}),
      });
      // Starting something new is an answer to the resume card.
      resumeRef.current = null;
      setResumeSnapshot(null);
    },
    [store],
  );

  const setMode = useCallback(
    (next: SessionMode) => {
      // Before the early return: choosing the mode you are already in is still a choice,
      // and it still has to stop the stored preference from arriving later and undoing it.
      modeChosenRef.current = true;
      if (modeRef.current === next) return;
      modeRef.current = next;
      setModeState(next);
      // The tap that got here is the user gesture, which is the only moment Safari will
      // resume a suspended context or open a microphone — so both happen on this stack.
      const recognizer = recognizerRef.current;
      if (next === 'on-the-go') {
        if (recognizer) {
          recognizer.pause();
          // On the Go's promise is "no microphone". Pausing alone honours that for the
          // reducer and not at all for the person: the OS recording indicator stays lit
          // in the one mode that exists to not be listening. So the recogniser is stopped
          // and the stream handed back, and coming back is a full re-acquisition.
          void recognizer.stop().catch((error: unknown) => report(error, 'recognizer'));
          micRef.current?.release();
          micReleasedRef.current = true;
        }
      } else {
        recognizer?.resume();
        if (recognizer && micReleasedRef.current) {
          micReleasedRef.current = false;
          void openMicrophone(recognizer);
        }
      }
      const save = optionsRef.current.saveMode ?? saveSessionMode;
      void save(next).catch((error: unknown) => report(error, 'mode-preference'));
    },
    [openMicrophone, report],
  );

  const startListening = useCallback(
    async (opts: { intention?: string } = {}) => {
      // This call does its own acquiring, so `setMode` must not also do it: clearing the
      // flag first is what stops a switch back from On the Go asking twice.
      micReleasedRef.current = false;
      setMode('piano');
      // A non-empty path means a session already exists — restored from a snapshot or
      // continued from a sketch — and "start listening" then means exactly that: open
      // the microphone over what is already there. SESSION_STARTED resets the path, so
      // dispatching it here would destroy the very progression the player came back for.
      if (store.getState().path.length === 0) startSession(opts.intention);

      await openMicrophone(ensureRecognizer());
    },
    [ensureRecognizer, openMicrophone, setMode, startSession, store],
  );

  const startOnTheGo = useCallback(
    (opts?: { intention?: string }) => {
      // No microphone, no AudioContext, no factories touched. See the file header.
      setMode('on-the-go');
      const current = store.getState();
      // A denial is a fact about the piano-mode gate, not about a train. Left in place
      // it makes SESSION_STARTED come up with `listening: 'idle'`, which the On the Go
      // screen derivation reads as "no session" — so "Continue on the go" from a denied
      // microphone would bounce back to home forever. Clear it before starting; it must
      // land before SESSION_STARTED because the reducer reads micStatus at that moment.
      if (current.micStatus === 'denied' || current.micStatus === 'unavailable') {
        store.dispatch({ type: 'MIC_STATUS', status: 'unrequested' });
      }
      // Same path-preservation rule as startListening: an existing progression is never
      // implicitly discarded by choosing how to continue it.
      if (current.path.length === 0) startSession(opts?.intention);
    },
    [setMode, startSession, store],
  );

  // -- selecting -----------------------------------------------------------

  const selectChord = useCallback(
    (chord: CanonicalChord) => {
      commitChordSelection(store, chord, optionsRef.current.now ?? performanceNow);
    },
    [store],
  );

  /**
   * Hearing a chord without taking it.
   *
   * Everything the landing sound does, minus the landing: the same `playChord` seam, the
   * same voicing, the same lazily-built sampler — so the chord a player auditions from a
   * card is note for note the chord that arrives if they take it. Nothing is dispatched,
   * because nothing has happened; this is the one path in the app that touches the audio
   * output and not the reducer.
   *
   * Which is also why it is not hung off the store the way the landing sound is. That
   * subscription exists because four affordances reach one `CHORD_DETECTED` and hooking
   * the buttons would mean four places to forget. A preview reaches no action at all, so
   * there is nothing to subscribe to — it is a capability, and the mode gate is on the
   * view rather than in here.
   */
  const previewChord = useCallback((chord: CanonicalChord) => {
    const play = optionsRef.current.playChord ?? playChord;
    play(chord);
  }, []);

  const stepBackTo = useCallback(
    (nodeId: string) => {
      const { path } = store.getState();
      const index = path.findIndex((node) => node.id === nodeId);
      if (index < 0) return;
      const count = path.length - 1 - index;
      // Tapping the node you are already on is not a request to lose it.
      if (count > 0) store.dispatch({ type: 'STEP_BACK', count });
    },
    [store],
  );

  // -- branches and prose ---------------------------------------------------

  const retryBranches = useCallback((nodeId: string, opts?: { hint?: string }) => {
    runtimeRef.current?.effects.requestBranchesFor(nodeId, opts);
  }, []);

  const fetchExplanation = useCallback<ExplanationFetcher>(
    (suggestion, signal) => {
      /*
       * With the companion off the paragraph is not fetched, it is recalled.
       *
       * `generateHeuristicBranchSet` resolved it against this exact pair of chords at the
       * moment the set was made, so there is nothing to compute and nothing to wait for —
       * `useExplanation`'s own cache then keeps it for the second and third tap exactly as
       * it does a fetched one.
       *
       * A rejection is the honest answer for a suggestion this device did not author: a
       * sketch reopened after a reload, a card from a stretch with the model on. The card
       * and the history detail both already have a line for that, and it is the same line
       * they show when the network is the thing that could not answer.
       */
      if (!settingsRef.current.aiCompanion) {
        const local = heuristicExplanation(suggestion.id);
        return local === null
          ? Promise.reject(new Error('no built-in explanation for this suggestion'))
          : Promise.resolve(local);
      }
      const current = store.getState();
      const from = currentNode(current);
      const { modelChoice, showPrompts } = settingsRef.current;
      const request: ExplainRequest = {
        target: 'suggestion',
        suggestion,
        toSymbol: suggestion.displaySymbol,
        keyEstimate: current.keyEstimate,
        perspectives: [...current.perspectives],
        ...(from ? { fromSymbol: from.chord.symbol } : {}),
        ...(current.intention === undefined ? {} : { intention: current.intention }),
        modelChoice,
        // Omitted rather than sent as `false`: with the setting off this is the request
        // that has always been sent, field for field.
        ...(showPrompts ? { debug: true } : {}),
      };
      return explain(request, {
        signal,
        onDebug: (debug) => recordDebugEntry('explain', debug),
      });
    },
    [store],
  );

  // -- keeping things -------------------------------------------------------

  const saveSketch = useCallback(
    async (
      scope: 'progression' | 'last-transition' = 'progression',
    ): Promise<SaveReceipt | null> => {
      const current = store.getState();
      if (current.path.length === 0) return null;
      const nodes = scope === 'last-transition' ? current.path.slice(-2) : current.path;
      const extras: Partial<NewSketch> = { nodes };
      // A saved transition wants to be findable as the move it was, not as whatever the
      // session's intention happened to say.
      if (scope === 'last-transition' && nodes.length === 2) {
        extras.title = `${nodes[0].chord.symbol} → ${nodes[1].chord.symbol}`;
      }
      try {
        const sketch = await createSketchFromSession(current, extras);
        const receipt: SaveReceipt = {
          sketchId: sketch.id,
          title: sketch.title,
          savedAt: sketch.updatedAt,
        };
        setLastSave(receipt);
        return receipt;
      } catch (error) {
        report(error, 'save');
        return null;
      }
    },
    [report, store],
  );

  const renameSketch = useCallback(
    (sketchId: string, title: string) => {
      void updateSketch(sketchId, { title })
        .then((updated) => {
          if (!updated) return;
          setLastSave((receipt) =>
            receipt && receipt.sketchId === sketchId
              ? { ...receipt, title: updated.title }
              : receipt,
          );
        })
        .catch((error: unknown) => report(error, 'rename'));
    },
    [report],
  );

  const dismissSave = useCallback(() => setLastSave(null), []);

  /**
   * "I like that transition" — the move just made, folded into the preference hints.
   *
   * The transition is resolved here rather than at the command bar because the path in
   * the store is the only authority on what "that" was: the bar's view is a render-old
   * snapshot, and a chord landing between the utterance and the tap would make it the
   * wrong pair. Pitch-class sets, not symbols, because that is the only matching key
   * (see rule 2) — a liked B♭7 → E♭maj7 must still read as liked when it was spelled
   * A♯7 → D♯maj7.
   *
   * Returns false when there is nothing to like yet, so the bar can be honest about it.
   */
  const markLiked = useCallback((): boolean => {
    const controller = runtimeRef.current?.preferences;
    if (!controller) return false;
    const { path } = store.getState();
    if (path.length < 2) return false;
    const [from, to] = path.slice(-2);
    controller.likeTransition(from.chord.pitchClasses, to.chord.pitchClasses);
    return true;
  }, [store]);

  const viewSketch = useCallback(
    (sketchId: string) => {
      navigate?.(`/sketchbook/${sketchId}`);
    },
    [navigate],
  );

  /**
   * Everything that has been kept. Without this the sketchbook is only reachable from the
   * save toast, which lives for four seconds — an hour of doodling would be findable only
   * in the four seconds after it was saved.
   */
  const viewSketchbook = useCallback(() => {
    navigate?.('/sketchbook');
  }, [navigate]);

  // -- resuming -------------------------------------------------------------

  const resumeSession = useCallback(() => {
    const snapshot = resumeRef.current;
    if (!snapshot) return;
    resumeRef.current = null;
    setResumeSnapshot(null);
    store.dispatch({ type: 'RESTORED', state: snapshot });
  }, [store]);

  const startFresh = useCallback(() => {
    const snapshot = resumeRef.current;
    resumeRef.current = null;
    setResumeSnapshot(null);
    if (!snapshot) return;
    // Declining once should not mean being asked again on the next cold start.
    void runtimeRef.current?.persistence
      .forget(snapshot.sessionId)
      .catch((error: unknown) => report(error, 'forget'));
  }, [report]);

  /** Whatever was on screen was thrown away on purpose; do not offer it back. */
  const forgetSession = useCallback(
    (sessionId: string) => {
      void runtimeRef.current?.persistence
        .forget(sessionId)
        .catch((error: unknown) => report(error, 'forget'));
    },
    [report],
  );

  /**
   * Start over without leaving the session screen.
   *
   * The mode, the microphone and the lenses all stay exactly as they are — this is the
   * "that went nowhere, let me try again" gesture, made with your hands still on the keys,
   * and making it cost a trip through the home screen would be the wrong shape for it.
   */
  const newSession = useCallback(() => {
    const { sessionId } = store.getState();
    // Dispatch first: it replaces the pending autosave with the empty session, so the
    // write that is being forgotten cannot land after the delete.
    startSession();
    forgetSession(sessionId);
  }, [forgetSession, startSession, store]);

  /**
   * Leave, all the way back to the entry screen.
   *
   * `RESTORED` rather than `SESSION_STARTED`, and the difference is the whole point:
   * `SESSION_STARTED` keeps `micStatus` and promotes `listening` to
   * `'awaiting-first-chord'`, which `screenFor` reads — correctly — as a session in
   * progress. Home is not a session in progress; it is the shape a cold start has, and
   * that shape is a whole `SessionState`. The action's contract is "replaces state
   * wholesale; validate before dispatching", and this one is valid by construction rather
   * than by having survived a round trip through IndexedDB.
   *
   * The lenses and the adventurousness ride across, exactly as `SESSION_STARTED` carries
   * them: they are settings the player chose, not part of the progression being discarded.
   */
  const endSession = useCallback(() => {
    const previous = store.getState();
    // Hand the microphone back. A home screen with the OS recording indicator still lit is
    // the same lie On the Go is careful not to tell, and the next `startListening` is a
    // user gesture, which is the only moment a stream can be reopened anyway.
    const recognizer = recognizerRef.current;
    if (recognizer) {
      recognizer.pause();
      void recognizer.stop().catch((error: unknown) => report(error, 'recognizer'));
      micRef.current?.release();
      micReleasedRef.current = true;
    }
    store.dispatch({
      type: 'RESTORED',
      state: createInitialSessionState({
        sessionId: newSessionId(),
        startedAt: Date.now(),
        perspectives: [...previous.perspectives],
        adventurousness: previous.adventurousness,
      }),
    });
    resumeRef.current = null;
    setResumeSnapshot(null);
    forgetSession(previous.sessionId);
  }, [forgetSession, report, store]);

  // -- settings and the recogniser's remote control -------------------------

  const updateSettings = useCallback(
    (patch: Partial<SessionSettings>) => {
      setSettings((current) => ({ ...current, ...patch }));

      // `speakReplies` is owned by `commands/voice.ts` (localStorage, so it can be read
      // before any database opens) and `prefetch` is deliberately per-session — a
      // token-saving choice made for one sitting. Everything else is a device preference
      // and is written below.
      if (patch.collectRecognitions !== undefined) {
        collectChosenRef.current = true;
        const save = optionsRef.current.saveCollect ?? saveCollectRecognitions;
        void save(patch.collectRecognitions).catch((error: unknown) =>
          report(error, 'collect-preference'),
        );
      }

      const stored: HostSettingsRecord = {};
      if (patch.soundOnTheGo !== undefined) stored.soundOnTheGo = patch.soundOnTheGo;
      if (patch.aiCompanion !== undefined) stored.aiCompanion = patch.aiCompanion;
      if (patch.showPrompts !== undefined) stored.showPrompts = patch.showPrompts;
      if (patch.modelChoice !== undefined) stored.modelChoice = patch.modelChoice;
      if (Object.keys(stored).length === 0) return;
      settingsChosenRef.current = true;
      const save = optionsRef.current.saveSettings ?? saveHostSettings;
      void save(stored).catch((error: unknown) => report(error, 'host-settings'));
    },
    [report],
  );

  const recognizerControls = useMemo(
    () => ({
      pause: () => recognizerRef.current?.pause(),
      resume: () => recognizerRef.current?.resume(),
    }),
    [],
  );

  /**
   * The settings sheet's "forget what you have learned", routed through the controller.
   *
   * Clearing the stored hints alone would be undone by the running controller on the next
   * chord: it keeps the tallies in memory and rewrites them. `reset()` clears both.
   */
  const resetPreferences = useCallback(async (): Promise<PreferenceHints> => {
    const controller = runtimeRef.current?.preferences;
    return controller ? controller.reset() : resetStoredPreferences();
  }, []);

  return useMemo<SessionView>(
    () => ({
      state,
      dispatch: store.dispatch,
      mode,
      setMode,
      startListening,
      startOnTheGo,
      selectChord,
      // Typing a chord and tapping one are the same act reached from two affordances.
      onManualChord: selectChord,
      // Offered exactly where a sound is welcome, and withheld — rather than ignored —
      // everywhere else, because the UI's only gate is the absence of the capability. At
      // the piano the speaker would be talking to the recogniser; with Sound off the
      // player has already said no to this app making a noise, and a button labelled
      // "Hear it" that respected that by doing nothing would be worse than no button.
      ...(mode === 'on-the-go' && settings.soundOnTheGo ? { previewChord } : {}),
      stepBackTo,
      retryBranches,
      fetchExplanation,
      resumeSnapshot,
      resumeSession,
      startFresh,
      endSession,
      newSession,
      lastSave,
      dismissSave,
      saveSketch,
      renameSketch,
      markLiked,
      ...(navigate ? { viewSketch, viewSketchbook } : {}),
      recognizerControls: recognizerLive ? recognizerControls : null,
      // The recogniser itself, for the calibration sheet: it needs the diagnostics stream
      // and `setParams`, which pause/resume cannot give it. Omitted rather than nulled
      // until one exists, so a view without a microphone simply has no such capability.
      ...(recognizerLive && recognizerRef.current ? { recognizer: recognizerRef.current } : {}),
      settings,
      updateSettings,
      resetPreferences,
    }),
    [
      dismissSave,
      endSession,
      fetchExplanation,
      lastSave,
      markLiked,
      mode,
      newSession,
      navigate,
      previewChord,
      recognizerControls,
      recognizerLive,
      renameSketch,
      resetPreferences,
      resumeSession,
      resumeSnapshot,
      retryBranches,
      saveSketch,
      selectChord,
      setMode,
      settings,
      startFresh,
      startListening,
      startOnTheGo,
      state,
      stepBackTo,
      store,
      updateSettings,
      viewSketch,
      viewSketchbook,
    ],
  );
}

export interface SessionHostProps extends SessionHostOptions {
  children?: ReactNode;
}

/**
 * Mount the live session and put it in context.
 *
 * Rendered once, above the router's outlet, so the session survives navigation to the
 * sketchbook and back — which is the whole reason it is not inside `SessionPage`.
 */
export function SessionHost({ children, ...options }: SessionHostProps): React.JSX.Element {
  const view = useLiveSessionView(options);
  return <SessionProvider value={view}>{children}</SessionProvider>;
}

export default SessionHost;
